import {
  BankOutlined,
  BarChartOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DollarOutlined,
  EyeOutlined,
  FileTextOutlined,
  GiftOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  SyncOutlined,
  TeamOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';

export type MenuItem = Required<MenuProps>['items'][number];

export const adminMenuItems: MenuItem[] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Tổng quan',
    children: [
      { key: '/dashboard', label: 'Tổng quan hệ thống' },
      { key: '/analytics', label: 'Phân tích nền tảng' },
    ],
  },
  {
    key: 'tenants',
    icon: <ShopOutlined />,
    label: 'Khách thuê',
    children: [
      { key: '/tenants', label: 'Danh sách khách thuê' },
    ],
  },
  // EG-52 — Tổ chức & CRM
  {
    key: 'org-crm',
    icon: <TeamOutlined />,
    label: 'Tổ chức & CRM',
    children: [
      { key: '/staffs', icon: <UserOutlined />, label: 'Nhân viên' },
      { key: '/stores', icon: <ShopOutlined />, label: 'Cửa hàng' },
      { key: '/customers', icon: <UserOutlined />, label: 'Khách hàng' },
      { key: '/vouchers', icon: <GiftOutlined />, label: 'Voucher' },
      { key: '/banks', icon: <BankOutlined />, label: 'Ngân hàng' },
      { key: '/bidv-refund-banks', icon: <BankOutlined />, label: 'BIDV refund bank' },
    ],
  },
  {
    key: 'orders',
    icon: <ShoppingCartOutlined />,
    label: 'Đơn hàng',
    children: [
      { key: '/orders', label: 'Tất cả đơn hàng' },
    ],
  },
  {
    key: 'misa',
    icon: <SyncOutlined />,
    label: 'Đồng bộ MISA',
    children: [
      { key: '/misa', label: 'Tổng quan MISA' },
    ],
  },
  {
    key: 'billing',
    icon: <DollarOutlined />,
    label: 'Thanh toán',
    children: [
      { key: '/billing', label: 'Tổng quan thanh toán' },
      { key: '/plans', label: 'Quản lý gói cước' },
      { key: '/billing/subscriptions', label: 'Gói đăng ký' },
      { key: '/billing/invoices', label: 'Hóa đơn' },
      { key: '/billing/failed-payments', label: 'Thanh toán lỗi' },
    ],
  },
  {
    key: 'reports',
    icon: <BarChartOutlined />,
    label: 'Báo cáo',
    children: [
      { key: '/reports', label: 'Báo cáo nền tảng' },
    ],
  },
  {
    key: 'monitoring',
    icon: <EyeOutlined />,
    label: 'Giám sát',
    children: [
      { key: '/monitoring/audit', label: 'Nhật ký kiểm toán' },
    ],
  },
  {
    key: 'support',
    icon: <CustomerServiceOutlined />,
    label: 'Hỗ trợ',
    children: [
      { key: '/support/tickets', label: 'Phiếu hỗ trợ' },
    ],
  },
  {
    key: 'system',
    icon: <ToolOutlined />,
    label: 'Hệ thống',
    children: [
      { key: '/system/accounts', label: 'Tài khoản và phân quyền' },
      { key: '/system/settings', label: 'Cài đặt' },
      { key: '/system/queues', label: 'Giám sát hàng đợi' },
      { key: '/menu', label: 'Quản lý menu' },
      { key: '/notification-templates', label: 'Template thông báo' },
      { key: '/zalo-zns-templates', label: 'Template ZNS' },
      { key: '/email-templates', label: 'Template email' },
    ],
  },
  {
    key: 'content',
    icon: <FileTextOutlined />,
    label: 'Nội dung',
    children: [
      { key: '/content/pages', icon: <FileTextOutlined />, label: 'Trang' },
      { key: '/content/articles', icon: <UnorderedListOutlined />, label: 'Bài viết' },
      { key: '/content/article-categories', icon: <UnorderedListOutlined />, label: 'Danh mục bài viết' },
    ],
  },
];

interface FlatMenuItem {
  key: string;
  label: string;
}

const flattenMenuItems = (items: MenuItem[]): FlatMenuItem[] => {
  const result: FlatMenuItem[] = [];

  items.forEach((item) => {
    if (!item || !('key' in item) || typeof item.key !== 'string') {
      return;
    }

    if ('children' in item && item.children && Array.isArray(item.children)) {
      result.push(...flattenMenuItems(item.children as MenuItem[]));
      return;
    }

    if ('label' in item && typeof item.label === 'string') {
      result.push({ key: item.key, label: item.label });
    }
  });

  return result;
};

export const flatAdminMenuItems = flattenMenuItems(adminMenuItems);

export const getAdminMenuGroupKey = (pathname: string) => {
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/analytics')) return 'dashboard';
  if (pathname.startsWith('/tenants')) return 'tenants';
  if (
    pathname.startsWith('/staffs') ||
    pathname.startsWith('/stores') ||
    pathname.startsWith('/customers') ||
    pathname.startsWith('/vouchers') ||
    pathname.startsWith('/banks') ||
    pathname.startsWith('/bidv-refund-banks')
  ) return 'org-crm';
  if (pathname.startsWith('/orders')) return 'orders';
  if (pathname.startsWith('/misa')) return 'misa';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/plans')) return 'billing';
  if (pathname.startsWith('/reports')) return 'reports';
  if (pathname.startsWith('/monitoring')) return 'monitoring';
  if (pathname.startsWith('/support')) return 'support';
  if (pathname.startsWith('/system')) return 'system';
  if (pathname.startsWith('/content')) return 'content';
  if (pathname.startsWith('/menu')) return 'system';
  if (pathname.startsWith('/notification-templates')) return 'system';
  if (pathname.startsWith('/zalo-zns-templates')) return 'system';
  if (pathname.startsWith('/email-templates')) return 'system';
  return '';
};

export const getAdminSelectedKey = (pathname: string) => {
  const exact = flatAdminMenuItems.find((item) => item.key === pathname)?.key;
  if (exact) {
    return exact;
  }

  const matchedPrefix = flatAdminMenuItems
    .map((item) => item.key)
    .filter((key) => pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];

  return matchedPrefix || pathname;
};

const routeTitleOverrides: Record<string, string> = {
  '/tenants': 'Danh sách khách thuê',
  '/orders': 'Tất cả đơn hàng',
  '/billing': 'Tổng quan thanh toán',
  '/misa': 'Tổng quan MISA',
  '/content/pages': 'Trang',
  '/content/articles': 'Bài viết',
  '/content/article-categories': 'Danh mục bài viết',
};

export const getAdminPageTitle = (pathname: string) => {
  const fromMenu = flatAdminMenuItems.find((item) => item.key === pathname)?.label;
  if (fromMenu) {
    return fromMenu;
  }

  const fromOverride = routeTitleOverrides[pathname];
  if (fromOverride) {
    return fromOverride;
  }

  if (pathname.startsWith('/tenants/')) return 'Chi tiết khách thuê';
  if (pathname.startsWith('/orders/')) return 'Chi tiết đơn hàng';
  if (pathname.startsWith('/billing/invoices/')) return 'Chi tiết hóa đơn';
  if (pathname.startsWith('/plans/') && pathname.endsWith('/tenants')) return 'Tenant theo gói';
  if (pathname.startsWith('/misa/')) return 'Chi tiết tiến trình MISA';
  if (pathname.startsWith('/staffs/')) return 'Chi tiết nhân viên';
  if (pathname.startsWith('/stores/')) return 'Chi tiết cửa hàng';
  if (pathname.startsWith('/customers/')) return 'Chi tiết khách hàng';
  if (pathname.startsWith('/vouchers/')) return 'Chi tiết voucher';
  if (pathname.startsWith('/banks/')) return 'Chi tiết ngân hàng';
  if (pathname.startsWith('/bidv-refund-banks/')) return 'Chi tiết BIDV refund bank';
  if (pathname.startsWith('/email-templates/') && pathname.endsWith('/edit')) return 'Sửa template email';
  if (pathname === '/email-templates/add') return 'Thêm template email';

  return 'Quản trị nền tảng';
};

const fallbackSegmentLabel = (segment: string) => {
  return segment
    .replace(/[-_]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const toAdminBreadcrumbItems = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return [];
  }

  let currentPath = '';
  return segments.map((segment) => {
    currentPath += `/${segment}`;

    if (currentPath.startsWith('/tenants/') && currentPath !== '/tenants') {
      return { title: 'Chi tiết khách thuê' };
    }
    if (currentPath.startsWith('/orders/') && currentPath !== '/orders') {
      return { title: 'Chi tiết đơn hàng' };
    }
    if (currentPath.startsWith('/billing/invoices/') && currentPath !== '/billing/invoices') {
      return { title: 'Chi tiết hóa đơn' };
    }
    if (currentPath.startsWith('/misa/') && currentPath !== '/misa') {
      return { title: 'Chi tiết tiến trình MISA' };
    }
    if (currentPath.startsWith('/staffs/') && currentPath !== '/staffs') {
      return { title: 'Chi tiết nhân viên' };
    }
    if (currentPath.startsWith('/stores/') && currentPath !== '/stores') {
      return { title: 'Chi tiết cửa hàng' };
    }
    if (currentPath.startsWith('/customers/') && currentPath !== '/customers') {
      return { title: 'Chi tiết khách hàng' };
    }
    if (currentPath.startsWith('/vouchers/') && currentPath !== '/vouchers') {
      return { title: 'Chi tiết voucher' };
    }
    if (currentPath.startsWith('/banks/') && currentPath !== '/banks') {
      return { title: 'Chi tiết ngân hàng' };
    }
    if (currentPath.startsWith('/bidv-refund-banks/') && currentPath !== '/bidv-refund-banks') {
      return { title: 'Chi tiết BIDV refund bank' };
    }
    if (currentPath.startsWith('/email-templates/') && currentPath !== '/email-templates') {
      return { title: currentPath === '/email-templates/add' ? 'Thêm template email' : 'Sửa template email' };
    }

    return {
      title:
        flatAdminMenuItems.find((item) => item.key === currentPath)?.label ||
        routeTitleOverrides[currentPath] ||
        fallbackSegmentLabel(segment),
    };
  });
};
